import { IP } from '../configuracion.js';

const API_URL_BASE = `${IP}/tavpos/backend/lectura_cheques.php`;

/**
 * Función para leer la memoria desde el backend
 */
export const leerMemoria = async (property = 'template') => {
  const effectiveProperty = property || 'template';
  const API_URL = `${API_URL_BASE}?property=${encodeURIComponent(effectiveProperty)}`;
  try {
    const response = await fetch(API_URL, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "X-Property": effectiveProperty,
      },
      credentials: "include",
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al leer la memoria: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(`Error al leer la memoria (property: ${effectiveProperty}):`, error.message);
    return [];
  }
};

/**
 * Función para crear un nuevo cheque
 */
export const crearNuevoCheque = async (property = 'template', mesa, cliente, revenue, asiento) => {
  const effectiveProperty = property || 'template';
  if (!mesa || !asiento) {
    console.error("Datos incompletos para crear el cheque:", { mesa, asiento });
    return null;
  }

  const API_URL = `${API_URL_BASE}?property=${encodeURIComponent(effectiveProperty)}`;
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Property": effectiveProperty,
      },
      credentials: "include",
      body: JSON.stringify({
        action: "crear-cheque",
        mesa: mesa,
        cliente: asiento,
        revenue: revenue || "",
        asiento: cliente || "",
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al crear el cheque: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    if (!data.cheque) {
      throw new Error("El servidor no devolvió un cheque válido.");
    }

    return data.cheque;
  } catch (error) {
    console.error(`Error al crear el cheque (property: ${effectiveProperty}):`, error.message);
    return null;
  }
};

/**
 * Función para agregar un pedido a un cheque
 */
export const agregarPedidoACheque = async (property = 'template', mesa, pedido, cliente) => {
  const effectiveProperty = property || 'template';
  if (!mesa || !pedido || !Array.isArray(pedido)) {
    console.error("Datos incompletos o inválidos para agregar el pedido:", { mesa, pedido });
    return null;
  }

  const API_URL = `${API_URL_BASE}?property=${encodeURIComponent(effectiveProperty)}`;
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Property": effectiveProperty,
      },
      credentials: "include",
      body: JSON.stringify({
        action: "agregar-pedido",
        mesa: mesa,
        pedido: pedido,
        clienteQuePidio: cliente || "",
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al agregar el pedido: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    if (!data.pedido) {
      throw new Error("El servidor no devolvió un pedido válido.");
    }

    return data.pedido;
  } catch (error) {
    console.error(`Error al agregar el pedido (property: ${effectiveProperty}):`, error.message);
    return null;
  }
};

/**
 * Función para buscar un cheque abierto
 */
export const buscarChequeAbierto = async (property = 'template', mesa) => {
  const effectiveProperty = property || 'template';
  if (!mesa) {
    console.error("Mesa no especificada para buscar el cheque abierto.");
    return null;
  }

  const API_URL = `${API_URL_BASE}?property=${encodeURIComponent(effectiveProperty)}&action=buscar-cheque-abierto&mesa=${encodeURIComponent(mesa)}`;
  try {
    const response = await fetch(API_URL, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "X-Property": effectiveProperty,
      },
      credentials: "include",
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al buscar el cheque abierto: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const data = await response.json();
    return data.cheque || null;
  } catch (error) {
    console.error(`Error al buscar el cheque abierto (property: ${effectiveProperty}, mesa: ${mesa}):`, error.message);
    return null;
  }
};

/**
 * Función para buscar un cheque abierto por numero_cheque
 */
export const buscarChequePorNumero = async (property = 'template', numeroCheque, retryCount = 5, retryDelay = 2000) => {
  const effectiveProperty = property || 'template';
  if (!numeroCheque) {
    console.error("[buscarChequePorNumero] Número de cheque no especificado.");
    return null;
  }

  const API_URL = `${API_URL_BASE}?property=${encodeURIComponent(effectiveProperty)}`;
  let lastError = null;

  for (let attempt = 1; attempt <= retryCount; attempt++) {
    try {
      console.log(
        `[buscarChequePorNumero] Attempt ${attempt} to fetch cheques for property: ${effectiveProperty}, numero_cheque: ${numeroCheque}, URL: ${API_URL}`
      );

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-Property": effectiveProperty,
        },
        credentials: "include",
        signal: AbortSignal.timeout(10000), // Add timeout to prevent hanging
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "No response body");
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const text = await response.text();
      if (!text.trim() || text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html")) {
        throw new Error("Invalid response: HTML or empty");
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        throw new Error(`Failed to parse JSON: ${jsonError.message}`);
      }

      let cheques = [];
      if (Array.isArray(data)) {
        cheques = data;
      } else if (data.data && Array.isArray(data.data)) {
        cheques = data.data; // Handle {"success":true,"data":[...]} from logs
      } else if (data.cheques && Array.isArray(data.cheques)) {
        cheques = data.cheques; // Handle {"cheques":[...]}
      } else {
        throw new Error("Invalid response: Expected array, 'data', or 'cheques' property");
      }

      console.log(
        `[buscarChequePorNumero] Cheques received: ${cheques.length}`,
        cheques.map(c => ({ numero_cheque: c.numero_cheque, cerrado: c.cerrado }))
      );

      const cheque = cheques.find(
        (c) => Number(c.numero_cheque) === Number(numeroCheque) && c.cerrado === 'Pendiente'
      );

      if (!cheque) {
        throw new Error(`Cheque ${numeroCheque} no encontrado o no está Pendiente`);
      }

      console.log(`[buscarChequePorNumero] Cheque encontrado:`, cheque);
      return cheque;
    } catch (error) {
      lastError = error;
      console.error(
        `[buscarChequePorNumero] Error (property: ${effectiveProperty}, numero_cheque: ${numeroCheque}, attempt: ${attempt}):`,
        error.message,
        { url: API_URL, attempt, name: error.name, stack: error.stack }
      );

      if (attempt < retryCount) {
        console.log(`[buscarChequePorNumero] Retrying in ${retryDelay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }
    }
  }

  console.error(
    `[buscarChequePorNumero] Failed after ${retryCount} attempts:`,
    lastError
  );
  return null;
};